import { Bell, LogOut, Menu, Search, Settings as SettingsIcon, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useUI } from '../context/UIContext'
import { Avatar } from './Avatar'
import { Dropdown } from './Dropdown'
import { NotificationsPanel } from './NotificationsPanel'
import { cn } from '../lib/cn'

export function Header() {
  const { setMobileNavOpen, setCommandPaletteOpen, notificationsOpen, setNotificationsOpen, assistantOpen, setAssistantOpen } =
    useUI()
  const { notifications } = useAppData()
  const { currentUser, logout } = useAuth()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const unread = notifications.filter((n) => !n.read).length

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-shell-border bg-shell/90 px-4 backdrop-blur-sm sm:px-6">
      <button
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open navigation menu"
        className="rounded-lg p-2 text-shell-text hover:bg-shell-border/50 lg:hidden"
      >
        <Menu size={20} />
      </button>

      <button
        onClick={() => setCommandPaletteOpen(true)}
        className="flex h-9 flex-1 max-w-sm items-center gap-2 rounded-lg border border-shell-border bg-shell px-3 text-sm text-shell-text transition-colors hover:border-shell-text/30 sm:max-w-md"
      >
        <Search size={15} />
        <span className="hidden sm:inline">Search work, clients, people…</span>
        <span className="sm:hidden">Search...</span>
        <kbd className="ml-auto hidden items-center gap-0.5 rounded-md border border-shell-border bg-shell-border/30 px-1.5 py-0.5 text-[10px] font-medium text-shell-text sm:flex">
          &#8984;K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={() => setAssistantOpen(!assistantOpen)}
          aria-label="Ask Hapi"
          className={cn(
            'rounded-lg p-2 text-shell-text transition-colors hover:bg-shell-border/50',
            assistantOpen && 'bg-shell-border/50 text-brand-500',
          )}
        >
          <Sparkles size={19} />
        </button>
        <div className="relative">
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            aria-label="Notifications"
            className={cn(
              'relative rounded-lg p-2 text-shell-text transition-colors hover:bg-shell-border/50',
              notificationsOpen && 'bg-shell-border/50',
            )}
          >
            <Bell size={19} />
            {unread > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-[9px] font-bold text-white ring-2 ring-shell">
                {unread}
              </span>
            )}
          </button>
          <NotificationsPanel />
        </div>
        <Dropdown
          align="right"
          trigger={
            <span className="ml-1 inline-flex rounded-full transition-transform hover:scale-105">
              <Avatar name={currentUser?.name ?? '?'} color="#FC2265" size="sm" />
            </span>
          }
          header={
            currentUser && (
              <div className="flex items-center gap-2.5">
                <Avatar name={currentUser.name} color="#FC2265" size="sm" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-shell-text-active">{currentUser.name}</p>
                  <p className="truncate text-xs text-shell-text capitalize">{currentUser.role.toLowerCase()}</p>
                </div>
              </div>
            )
          }
          items={[
            { label: 'Settings', value: 'settings', icon: <SettingsIcon size={13} /> },
            { label: 'Sign out', value: 'signout', icon: <LogOut size={13} />, danger: true },
          ]}
          onSelect={(v) => {
            if (v === 'settings') navigate('/settings')
            if (v === 'signout') {
              handleLogout()
              showToast('You have been signed out.', 'info')
            }
          }}
        />
      </div>
    </header>
  )
}
